import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpClient } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { AuthenticationService } from '../services/auth.service';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  constructor(
    private http: HttpClient,
    private authService: AuthenticationService,
    private router: Router
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = sessionStorage.getItem('authToken');
    // Skip refresh logic for the RefreshToken API itself
    if (request.url.includes('Users/RefreshToken')) {
      if (token) {
        const cloned = request.clone({
          setHeaders: { Authorization: `Bearer ${token}` }
        });
        return next.handle(cloned);
      }
      return next.handle(request);
    }
    if (!token) {
      return next.handle(request);
    }
    // Call refresh token API before every request (except itself)
    return from(this.http.post<any>(`${environment.baseUrl}Users/RefreshToken`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    }).toPromise()
    .catch(err => {
      // If refresh API returns 401 or 'User Already logged out', logout and redirect
      if (err.status === 401 && err.error && (err.error.status === 'User Already logged out' || err.error === 'User Already logged out')) {
        this.authService.logout();
        this.router.navigate(['/auth/login']);
        throw err;
      }
      throw err;
    })
    ).pipe(
      switchMap((res: any) => {
        // If refresh API returns status 'User Already logged out', logout and redirect
        if (res && res.status === 'User Already logged out') {
          this.authService.logout();
          this.router.navigate(['/auth/login']);
          throw { status: 401, error: res };
        }
        if (res && res.token) {
          if (!this.authService.storeToken(res.token)) {
            this.router.navigate(['/auth/login']);
            throw { status: 401, error: 'Invalid or expired token' };
          }
          const cloned = request.clone({
            setHeaders: { Authorization: `Bearer ${res.token}` }
          });
          return next.handle(cloned).pipe(
            catchError(err => {
              if (err.status === 401) {
                this.authService.logout();
                this.router.navigate(['/auth/login']);
              }
              throw err;
            })
          );
        } else {
          // If no token in response, proceed with old token
          const cloned = request.clone({
            setHeaders: { Authorization: `Bearer ${token}` }
          });
          return next.handle(cloned).pipe(
            catchError(err => {
              if (err.status === 401) {
                this.authService.logout();
                this.router.navigate(['/auth/login']);
              }
              throw err;
            })
          );
        }
      }),
      catchError(err => {
        // On error, proceed with old token
        const cloned = request.clone({
          setHeaders: { Authorization: `Bearer ${token}` }
        });
        return next.handle(cloned).pipe(
          catchError(innerErr => {
            if (innerErr.status === 401) {
              this.authService.logout();
              this.router.navigate(['/auth/login']);
            }
            throw innerErr;
          })
        );
      })
    );
  }
}
